/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { jsx } from '@atlaskit/css';
import FeedbackCollector from '@atlaskit/feedback-collector';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import { CategoryPillGroup } from './category-pill-group';
import {
	EmojiRatingSelector,
	type ExperienceRating,
	ExperienceRatingToNumber,
} from './emoji-rating-selector';
import { messages } from './messages';
import { type SmartCreateFeedbackProps, SmartCreateFeedbackVariant } from './types';

const ANALYTICS_CHANNEL = 'aiMate';

const FEEDBACK_CONTEXT_FIELD_ID = 'customfield_10047';

const constructFeedbackContext = (
	additionalFields: object,
	aaid: string | undefined,
	tenantId: string | undefined,
) => {
	const userAgent = navigator.userAgent;
	const screenResolution = `${window.screen.width} x ${window.screen.height}`;

	const feedbackContext = {
		'User-Agent': userAgent,
		'Screen Resolution': screenResolution,
		'User Account Id': aaid ?? 'unknown',
		'Tenant Id': tenantId ?? 'unknown',
		/* Other context fields will have to be passed in as props, since there aren't public hooks to get these.
		Environment: metadata.environment ?? '',
		Product: metadata.product ?? '',
		Edition: metadata.edition ?? '',
		*/
		...additionalFields,
	};

	const feedbackContextStr = Object.entries(feedbackContext)
		.map(([key, value]) => `${key}: ${value}`)
		.join('\n');

	return [
		{
			id: FEEDBACK_CONTEXT_FIELD_ID,
			value: feedbackContextStr,
		},
	];
};

const getFeedbackConfig = (variant: SmartCreateFeedbackVariant) => {
	switch (variant) {
		case SmartCreateFeedbackVariant.CREATE_WITH_ROVO:
			return {
				feedbackTitle: messages.feedbackTitle,
				rateExperience: messages.rateExperience,
				entrypointId: '63867aef-06e5-4520-855f-4d29e062f22c',
			};
		case SmartCreateFeedbackVariant.ROVO:
			return {
				feedbackTitle: messages.feedbackTitleNoCreate,
				rateExperience: messages.rateExperienceNoCreate,
				entrypointId: '63867aef-06e5-4520-855f-4d29e062f22c',
			};
		case SmartCreateFeedbackVariant.CREATE_WITH_ROVO_FROM_JIRA:
			return {
				feedbackTitle: messages.feedbackTitle,
				rateExperience: messages.rateExperience,
				// https://customerfeedback.atlassian.net/jira/servicedesk/projects/JIRAAIFC
				entrypointId: 'e79ff340-38e9-4c53-9d3b-87a776964f2c',
			};
		case SmartCreateFeedbackVariant.REMIX:
			return {
				feedbackTitle: messages.feedbackTitleRemix,
				rateExperience: messages.rateExperienceRemix,
				entrypointId: '0953facd-d9b5-4ce6-b1d6-7bae93e09b2f',
			};
		case SmartCreateFeedbackVariant.SLIDES:
			return {
				feedbackTitle: messages.feedbackTitleSlides,
				rateExperience: messages.rateExperienceSlides,
				entrypointId: 'dd0e4a6a-6a39-40d4-961f-a5bf382c9fb0',
			};
		case SmartCreateFeedbackVariant.CREATE_WITH_ROVO_FROM_JSM:
			return {
				feedbackTitle: messages.feedbackTitle,
				rateExperience: messages.rateExperience,
				entrypointId: 'ad7943a4-aa8a-4f69-b317-7241ec65472c',
			};
		case SmartCreateFeedbackVariant.ROVO_INSIGHTS_FROM_JSM:
			return {
				feedbackTitle: messages.feedbackTitleRovoInsights,
				rateExperience: messages.rateExperienceRovoInsights,
				// https://customerfeedback.atlassian.net/jira/servicedesk/projects/JSAIF
				entrypointId: '122afbe6-2758-40f4-a24e-59c01eb0fd39',
			};
	}
};

export function SmartCreateFeedback(props: SmartCreateFeedbackProps): JSX.Element {
	const { name, aaid, email } = useProfileController();

	return <SmartCreateFeedbackInternal name={name} aaid={aaid} email={email} {...props} />;
}

type InternalProps = {
	name: string;
	aaid: string | undefined;
	email: string | undefined;
};
export function SmartCreateFeedbackInternal({
	variant = SmartCreateFeedbackVariant.CREATE_WITH_ROVO,
	onClose,
	onSubmit,
	tenantId,
	additionalFields = {},
	name,
	aaid,
	email,
	customContent,
	showDefaultTextFields = true,
	summaryDefaultValue,
	descriptionDefaultValue,
	categories: categoriesProp,
}: SmartCreateFeedbackProps & InternalProps): JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { feedbackTitle, rateExperience, entrypointId } = getFeedbackConfig(variant);

	const defaultCategories = useMemo(
		() => [
			{ id: 'performance', text: formatMessage(messages.categoryPerformance) },
			{ id: 'response-quality', text: formatMessage(messages.categoryResponseQuality) },
			{ id: 'user-experience', text: formatMessage(messages.categoryUserExperience) },
			{ id: 'other', text: formatMessage(messages.categoryOther) },
		],
		[formatMessage],
	);

	// Use caller-supplied categories when provided; fall back to the shared defaults.
	const categories = categoriesProp ?? defaultCategories;

	const [experienceRating, setExperienceRating] = useState<ExperienceRating | undefined>(undefined);
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'smartCreateFeedbackCollector',
			attributes: {
				feedbackVariant: variant,
			},
			tags: ['atlaskit'],
		}).fire(ANALYTICS_CHANNEL);
	}, [variant, createAnalyticsEvent]);

	const onCloseWithAnalytics = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'closed',
			actionSubject: 'smartCreateFeedbackCollector',
			attributes: {
				feedbackVariant: variant,
			},
			tags: ['atlaskit'],
		}).fire(ANALYTICS_CHANNEL);
		onClose();
	}, [onClose, variant, createAnalyticsEvent]);

	const onSubmitWithAnalytics = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'submitted',
			actionSubject: 'smartCreateFeedbackCollector',
			attributes: {
				feedbackVariant: variant,
				experienceRating,
				selectedCategory,
			},
			tags: ['atlaskit'],
		}).fire(ANALYTICS_CHANNEL);
		onSubmit();
	}, [onSubmit, variant, experienceRating, selectedCategory, createAnalyticsEvent]);

	const customFields = {
		'Feedback Variant': variant,
		'Selected Category': selectedCategory ?? '',
		'Experience Rating': experienceRating ?? '',
		'Experience Rating Number':
			experienceRating !== undefined ? ExperienceRatingToNumber[experienceRating] : '',
		...additionalFields,
	};

	return (
		<FeedbackCollector
			onClose={onCloseWithAnalytics}
			onSubmit={onSubmitWithAnalytics}
			atlassianAccountId={aaid}
			name={name}
			email={email}
			entrypointId={entrypointId}
			feedbackTitle={formatMessage(feedbackTitle)}
			showTypeField={false}
			showDefaultTextFields={showDefaultTextFields}
			summaryPlaceholder={formatMessage(messages.summaryPlaceholderText)}
			customTextAreaLabel={formatMessage(messages.summaryTitle)}
			customContent={
				<>
					<EmojiRatingSelector
						rating={experienceRating}
						setRating={setExperienceRating}
						label={formatMessage(rateExperience)}
					/>
					<CategoryPillGroup
						categories={categories}
						selectedCategory={selectedCategory}
						setSelectedCategory={setSelectedCategory}
						label={formatMessage(messages.categoryTitle)}
					/>
					{customContent}
				</>
			}
			descriptionDefaultValue={descriptionDefaultValue}
			summaryDefaultValue={summaryDefaultValue}
			disableSubmitButton={experienceRating === undefined}
			additionalFields={constructFeedbackContext(customFields, aaid, tenantId)}
		/>
	);
}
